export type MapSelection = {
    geozoneGroups: Array<{
        groupId: string;
        showAll: boolean;
        geozoneIds: string[];
    }>;
    containersSites: Array<{
        geozoneId: string;
        showAll: boolean;
        checkpointIds: string[];
    }>;
    vehicle: Array<{
        contractorId: string;
        showAll: boolean;
        machineIds: string[];
    }>;
};

const STORAGE_KEY = "mapSelection";

const emptySelection: MapSelection = {
    geozoneGroups: [],
    containersSites: [],
    vehicle: [],
};

function normalizeId(value: unknown): string {
    return String(value ?? "").trim();
}

function normalizeIdArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    const result: string[] = [];
    const seen = new Set<string>();
    for (const item of value) {
        const id = normalizeId(item);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        result.push(id);
    }
    return result;
}

function sanitizeSelection(raw: any): MapSelection {
    const safe = raw && typeof raw === "object" ? raw : {};

    const geozoneGroups = Array.isArray(safe.geozoneGroups) ? safe.geozoneGroups : [];
    const containersSites = Array.isArray(safe.containersSites) ? safe.containersSites : [];
    const vehicle = Array.isArray(safe.vehicle) ? safe.vehicle : [];

    return {
        geozoneGroups: geozoneGroups.map((item: any) => {
            const groupId = normalizeId(item?.groupId);
            const showAll = Boolean(item?.showAll);
            const geozoneIds = showAll ? [] : normalizeIdArray(item?.geozoneIds);
            return { groupId, showAll, geozoneIds };
        }).filter((item: any) => item.groupId && (item.showAll || item.geozoneIds.length > 0)),
        containersSites: containersSites.map((item: any) => {
            const geozoneId = normalizeId(item?.geozoneId);
            const showAll = Boolean(item?.showAll);
            const checkpointIds = showAll ? [] : normalizeIdArray(item?.checkpointIds);
            return { geozoneId, showAll, checkpointIds };
        }).filter((item: any) => item.geozoneId && (item.showAll || item.checkpointIds.length > 0)),
        vehicle: vehicle.map((item: any) => {
            const contractorId = normalizeId(item?.contractorId);
            const showAll = Boolean(item?.showAll);
            const machineIds = showAll ? [] : normalizeIdArray(item?.machineIds);
            return { contractorId, showAll, machineIds };
        }).filter((item: any) => item.contractorId && (item.showAll || item.machineIds.length > 0)),
    };
}

export function getSelection(): MapSelection {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return emptySelection;
        const parsed = JSON.parse(raw);
        return sanitizeSelection(parsed);
    } catch {
        return emptySelection;
    }
}

export function saveSelection(next: MapSelection): void {
    const sanitized = sanitizeSelection(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
    window.dispatchEvent(new Event("map-selection-changed"));
}

function upsertById<T extends { id: string }>(items: T[], id: string, build: () => T, update: (item: T) => T): T[] {
    const idx = items.findIndex((item) => item.id === id);
    if (idx === -1) {
        return [...items, build()];
    }
    const next = [...items];
    next[idx] = update(next[idx]);
    return next;
}

function toggleId(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

export function toggleGroup(groupId: string): void {
    const id = normalizeId(groupId);
    if (!id) return;
    const selection = getSelection();

    const nextGroups = upsertById(
        selection.geozoneGroups.map((g) => ({ ...g, id: g.groupId })),
        id,
        () => ({ id, groupId: id, showAll: true, geozoneIds: [] }),
        (item) => ({
            ...item,
            groupId: item.groupId || item.id,
            showAll: !item.showAll,
            geozoneIds: item.showAll ? [] : item.geozoneIds,
        })
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, geozoneGroups: nextGroups });
}

export function toggleGeozoneInGroup(groupId: string, geozoneId: string, allGeozoneIds?: string[]): void {
    const gId = normalizeId(groupId);
    const zId = normalizeId(geozoneId);
    if (!gId || !zId) return;
    const selection = getSelection();

    const nextGroups = upsertById(
        selection.geozoneGroups.map((g) => ({ ...g, id: g.groupId })),
        gId,
        () => ({ id: gId, groupId: gId, showAll: false, geozoneIds: [zId] }),
        (item) => {
            let geozoneIds: string[];
            let showAll = item.showAll;
            
            if (item.showAll) {
                // Если было "выбрать все", то при отключении одного элемента
                // переключаемся в режим выбора по отдельности и выбираем все кроме этого
                showAll = false;
                if (allGeozoneIds) {
                    geozoneIds = allGeozoneIds.filter(id => id !== zId);
                } else {
                    geozoneIds = [];
                }
            } else {
                geozoneIds = toggleId(item.geozoneIds, zId);
            }
            
            return { ...item, groupId: item.groupId || item.id, showAll, geozoneIds };
        }
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, geozoneGroups: nextGroups });
}

export function toggleGeozone(geozoneId: string): void {
    const id = normalizeId(geozoneId);
    if (!id) return;
    const selection = getSelection();

    const nextContainersSites = upsertById(
        selection.containersSites.map((g) => ({ ...g, id: g.geozoneId })),
        id,
        () => ({ id, geozoneId: id, showAll: true, checkpointIds: [] }),
        (item) => ({
            ...item,
            geozoneId: item.geozoneId || item.id,
            showAll: !item.showAll,
            checkpointIds: item.showAll ? [] : item.checkpointIds,
        })
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, containersSites: nextContainersSites });
}

export function toggleCheckpointInGeozone(geozoneId: string, checkpointId: string, allCheckpointIds?: string[]): void {
    const gId = normalizeId(geozoneId);
    const cId = normalizeId(checkpointId);
    if (!gId || !cId) return;
    const selection = getSelection();

    const nextContainersSites = upsertById(
        selection.containersSites.map((g) => ({ ...g, id: g.geozoneId })),
        gId,
        () => ({ id: gId, geozoneId: gId, showAll: false, checkpointIds: [cId] }),
        (item) => {
            let checkpointIds: string[];
            let showAll = item.showAll;
            
            if (item.showAll) {
                showAll = false;
                if (allCheckpointIds) {
                    checkpointIds = allCheckpointIds.filter(id => id !== cId);
                } else {
                    checkpointIds = [];
                }
            } else {
                checkpointIds = toggleId(item.checkpointIds, cId);
            }
            
            return { ...item, geozoneId: item.geozoneId || item.id, showAll, checkpointIds };
        }
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, containersSites: nextContainersSites });
}

export function toggleContractor(contractorId: string): void {
    const id = normalizeId(contractorId);
    if (!id) return;
    const selection = getSelection();

    const nextVehicle = upsertById(
        selection.vehicle.map((c) => ({ ...c, id: c.contractorId })),
        id,
        () => ({ id, contractorId: id, showAll: true, machineIds: [] }),
        (item) => ({
            ...item,
            contractorId: item.contractorId || item.id,
            showAll: !item.showAll,
            machineIds: item.showAll ? [] : item.machineIds,
        })
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, vehicle: nextVehicle });
}

export function toggleMachineInContractor(contractorId: string, machineId: string, allMachineIds?: string[]): void {
    const cId = normalizeId(contractorId);
    const mId = normalizeId(machineId);
    if (!cId || !mId) return;
    const selection = getSelection();

    const nextVehicle = upsertById(
        selection.vehicle.map((c) => ({ ...c, id: c.contractorId })),
        cId,
        () => ({ id: cId, contractorId: cId, showAll: false, machineIds: [mId] }),
        (item) => {
            let machineIds: string[];
            let showAll = item.showAll;
            
            if (item.showAll) {
                showAll = false;
                if (allMachineIds) {
                    machineIds = allMachineIds.filter(id => id !== mId);
                } else {
                    machineIds = [];
                }
            } else {
                machineIds = toggleId(item.machineIds, mId);
            }
            
            return { ...item, contractorId: item.contractorId || item.id, showAll, machineIds };
        }
    ).map(({ id: _id, ...rest }) => rest);

    saveSelection({ ...selection, vehicle: nextVehicle });
}

